import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Server,
  Database,
  Mail,
  CreditCard,
  Car,
  Cloud,
  Bell,
  Smartphone,
  Settings,
  RefreshCw,
  LayoutDashboard,
  Users,
  Store,
  Monitor,
  FileText,
  TrendingUp,
  LogOut,
  BarChart3,
  Gift,
  ListChecks,
  Megaphone,
  Shield,
  Clock,
  ExternalLink
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar navigation items
const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
  { label: "Registrations", icon: Users, path: "/admin/registrations" },
  { label: "Sites", icon: Monitor, path: "/admin/sites" },
  { label: "Retailers", icon: Store, path: "/admin/retailers" },
  { label: "Entitlements", icon: Gift, path: "/admin/entitlements" },
  { label: "Analytics", icon: BarChart3, path: "/admin/analytics" },
  { label: "Screens", icon: Monitor, path: "/admin/screens" },
  { label: "Content", icon: FileText, path: "/admin/content" },
  { label: "Playlists", icon: ListChecks, path: "/admin/playlists" },
  { label: "Advertisers", icon: Megaphone, path: "/admin/advertisers" },
  { label: "Revenue", icon: TrendingUp, path: "/admin/revenue" },
  { label: "Billing", icon: CreditCard, path: "/admin/billing" },
  { label: "Settings", icon: Settings, path: "/admin/settings" },
  { label: "System Status", icon: Activity, path: "/admin/system-status", active: true },
];

export default function AdminSystemStatus() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStatus = async () => {
    try {
      const [healthRes, integrationsRes, featuresRes] = await Promise.all([
        axios.get(`${API}/health`),
        axios.get(`${API}/integrations/status`),
        axios.get(`${API}/config/features`)
      ]);
      
      setHealth(healthRes.data);
      setIntegrations(integrationsRes.data);
      setFeatures(featuresRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch status:", error);
      toast.error("Failed to fetch system status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  const handleLogout = () => {
    document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    navigate('/admin/login');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'disabled':
        return <XCircle className="w-5 h-5 text-slate-400" />;
      case 'mocked':
      case 'local_storage':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'disabled':
        return 'bg-slate-50 border-slate-200 text-slate-500';
      case 'mocked':
      case 'local_storage':
        return 'bg-amber-50 border-amber-200 text-amber-700';
      default:
        return 'bg-slate-50 border-slate-200 text-slate-500';
    }
  };

  const getIntegrationIcon = (name) => {
    switch (name) {
      case 'email': return Mail;
      case 'stripe': return CreditCard;
      case 'anpr': return Car;
      case 'cloudinary': return Cloud;
      case 'push_notifications': return Bell;
      case 'apple_signin': return Smartphone;
      default: return Server;
    }
  };

  const integrationLabels = {
    email: { name: 'Email (Resend)', description: 'Transactional email delivery' },
    stripe: { name: 'Payments (Stripe)', description: 'Payment processing' },
    anpr: { name: 'ANPR', description: 'Vehicle recognition' },
    cloudinary: { name: 'Image Storage', description: 'Cloud image hosting' },
    push_notifications: { name: 'Push Notifications', description: 'Mobile notifications' },
    apple_signin: { name: 'Apple Sign-In', description: 'Apple authentication' }
  };

  // Calculate missing credentials
  const getMissingCredentials = () => {
    const missing = [];
    if (integrations?.integrations) {
      const intgs = integrations.integrations;
      if (intgs.email?.status === 'disabled') {
        missing.push({ name: 'Resend API Key', env: 'RESEND_API_KEY', critical: true });
      }
      if (intgs.stripe?.status === 'disabled') {
        missing.push({ name: 'Stripe API Key', env: 'STRIPE_API_KEY', critical: true });
      }
      if (intgs.anpr?.status === 'mocked') {
        missing.push({ name: 'ANPR Integration', env: 'ANPR_API_KEY', critical: false });
      }
    }
    return missing;
  };

  const missingCredentials = getMissingCredentials();

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-system-status">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col min-h-screen">
        <div className="p-4 border-b border-slate-800">
          <h1 className="text-xl font-bold">ParkWise</h1>
          <p className="text-sm text-slate-400">Admin Console</p>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    item.active 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">System Status</h1>
              <p className="text-slate-500">Launch readiness and integration health</p>
            </div>
            <div className="flex items-center gap-3">
              {lastRefresh && (
                <span className="text-sm text-slate-500">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                data-testid="refresh-status-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Health Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* API Health */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        health?.status === 'healthy' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {health?.status === 'healthy' ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">API Status</p>
                        <p className={`text-lg font-bold ${
                          health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {health?.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Demo Mode */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        features?.demo_mode ? 'bg-amber-100' : 'bg-green-100'
                      }`}>
                        {features?.demo_mode ? (
                          <AlertTriangle className="w-6 h-6 text-amber-600" />
                        ) : (
                          <Shield className="w-6 h-6 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Environment</p>
                        <p className={`text-lg font-bold ${
                          features?.demo_mode ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          {features?.demo_mode ? 'Demo Mode' : 'Production'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Launch Readiness */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        missingCredentials.filter(c => c.critical).length === 0 
                          ? 'bg-green-100' 
                          : 'bg-amber-100'
                      }`}>
                        <Activity className={`w-6 h-6 ${
                          missingCredentials.filter(c => c.critical).length === 0 
                            ? 'text-green-600' 
                            : 'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Launch Readiness</p>
                        <p className={`text-lg font-bold ${
                          missingCredentials.filter(c => c.critical).length === 0 
                            ? 'text-green-600' 
                            : 'text-amber-600'
                        }`}>
                          {missingCredentials.filter(c => c.critical).length === 0 
                            ? 'Ready' 
                            : `${missingCredentials.filter(c => c.critical).length} Items Pending`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Missing Credentials Alert */}
              {missingCredentials.length > 0 && (
                <Card className="mb-8 border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-900">
                      <AlertTriangle className="w-5 h-5" />
                      Missing Production Credentials
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      The following credentials are required for full production operation
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {missingCredentials.map((cred, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                        >
                          <div className="flex items-center gap-3">
                            {cred.critical ? (
                              <XCircle className="w-5 h-5 text-red-500" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-amber-500" />
                            )}
                            <div>
                              <p className="font-medium text-slate-900">{cred.name}</p>
                              <p className="text-sm text-slate-500 font-mono">{cred.env}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            cred.critical 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {cred.critical ? 'Required' : 'Recommended'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Integration Status */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-blue-600" />
                    Integration Status
                  </CardTitle>
                  <CardDescription>
                    Status of all external service integrations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {integrations?.integrations && Object.entries(integrations.integrations).map(([key, value]) => {
                      const Icon = getIntegrationIcon(key);
                      const label = integrationLabels[key] || { name: key, description: '' };
                      
                      return (
                        <div 
                          key={key}
                          className={`p-4 rounded-lg border ${getStatusColor(value.status)}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Icon className="w-5 h-5" />
                              <div>
                                <p className="font-medium">{label.name}</p>
                                <p className="text-sm opacity-80">{label.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(value.status)}
                              <span className="text-sm font-medium capitalize">
                                {value.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Feature Flags */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    Feature Flags
                  </CardTitle>
                  <CardDescription>
                    Current feature toggle status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {features && Object.entries(features).map(([key, value]) => {
                      if (key === 'demo_mode') return null;
                      if (typeof value !== 'object') return null;
                      
                      return (
                        <div 
                          key={key}
                          className={`p-3 rounded-lg border ${
                            value.enabled 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-700 capitalize">
                              {key.replace('_', ' ')}
                            </span>
                            {value.enabled ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-blue-600" />
                    Launch Documentation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <a 
                      href="/LAUNCH_READINESS.md" 
                      target="_blank"
                      className="p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">Launch Readiness</p>
                      <p className="text-sm text-slate-500">MVP status and go-live checklist</p>
                    </a>
                    <a 
                      href="/DEPLOYMENT_CHECKLIST.md" 
                      target="_blank"
                      className="p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">Deployment Checklist</p>
                      <p className="text-sm text-slate-500">Step-by-step deployment guide</p>
                    </a>
                    <a 
                      href="/INTEGRATIONS.md" 
                      target="_blank"
                      className="p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">Integration Reference</p>
                      <p className="text-sm text-slate-500">External service documentation</p>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
